import { useCallback, useRef, useEffect } from 'react';

interface UseAutoSyncOptionsRequestModel<T> {
    localData: T;
    onSync: (data: T) => Promise<void> | void;
    syncDelay?: number;
    enableAutoSync?: boolean;
}

export const useAutoSync = <T,>({
                                    localData,
                                    onSync,
                                    syncDelay = 1000,
                                    enableAutoSync = true
                                }: UseAutoSyncOptionsRequestModel<T>) => {
    //#region variables
    const isDirty = useRef(false);
    const syncTimeoutRef = useRef<NodeJS.Timeout>();
    const lastSyncedData = useRef<string>();
    const componentRef = useRef<HTMLDivElement>(null);
    const isSyncing = useRef(false); // Prevent multiple syncs
    //#endregion
    //#region state & hooks
    // Stable sync function that doesn't change on every render
    const syncNow = useCallback(async () => {
        if (!isDirty.current || !enableAutoSync || isSyncing.current) {
            return;
        }

        isSyncing.current = true;
        try {
            await onSync(localData);
            isDirty.current = false;
            lastSyncedData.current = JSON.stringify(localData);
            console.log('Data synced successfully');
        } catch (error) {
            console.error('Sync failed:', error);
        } finally {
            isSyncing.current = false;
        }
    }, [onSync, localData, enableAutoSync]);
    const handleComponentBlur = useCallback((e: React.FocusEvent) => {
        // Add small delay to ensure relatedTarget is set
        setTimeout(() => {
            const relatedTarget = e.relatedTarget as Node | null;
            const isLeavingComponent = !componentRef.current?.contains(relatedTarget);

            if (isLeavingComponent && isDirty.current && !isSyncing.current) {
                console.log('Component blur - syncing immediately');
                syncNow();
            }
        }, 10);
    }, [syncNow]);
    //#endregion
    //#region useEffect
    // Only track changes when localData actually changes
    useEffect(() => {
        const currentDataString = JSON.stringify(localData);
        // Skip if this is the first render or data hasn't changed
        if (!lastSyncedData.current) {
            lastSyncedData.current = currentDataString;
            return;
        }

        if (currentDataString !== lastSyncedData.current) {
            isDirty.current = true;

            // Clear existing timeout
            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current);
            }

            // Set new timeout for auto-sync
            if (enableAutoSync && syncDelay > 0) {
                syncTimeoutRef.current = setTimeout(() => {
                    syncNow();
                }, syncDelay);
            }
        }
    }, [localData, syncNow, syncDelay, enableAutoSync]);
    // Window blur backup - only sync if dirty
    useEffect(() => {
        const handleWindowBlur = () => {
            if (isDirty.current && enableAutoSync && !isSyncing.current) {
                console.log('Window blur - syncing');
                syncNow();
            }
        };

        window.addEventListener('blur', handleWindowBlur);
        return () => window.removeEventListener('blur', handleWindowBlur);
    }, [syncNow, enableAutoSync]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (syncTimeoutRef.current) {
                clearTimeout(syncTimeoutRef.current);
            }
            // Final sync on unmount if dirty
            if (isDirty.current && enableAutoSync && !isSyncing.current) {
                onSync(localData);
            }
        };
    }, []); // Empty dependency array - only run on unmount
    //#endregion
    return {
        handleComponentBlur,
        isDirty: isDirty.current,
        componentRef
    };
};

export {};